import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { getToken } from "../lib/api";
import Layout from "../components/Layout";
import { MUSCLE_LABELS } from "../components/BodyDiagram";
import { AreasWorkedCards } from "../components/AreasWorkedCards";
import AuthImage from "../components/AuthImage";
import SessionPhotosManager from "../components/SessionPhotosManager";
import SendToClientModal from "../components/session/SendToClientModal";
import { ArrowLeft, Calendar, AlertCircle, Download, Printer, Mail, FileText, X, Send, Upload } from "lucide-react";
import { toast } from "sonner";
import DisclaimerBanner from "../components/DisclaimerBanner";

const EMPTY_FOCUS = [];

export default function SessionView() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [client, setClient] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sendRecsOpen, setSendRecsOpen] = useState(false);

  useEffect(() => {
    api.get(`/sessions/${id}`).then((r) => {
      setS(r.data);
      api.get(`/clients/${r.data.client_id}`).then((c) => setClient(c.data)).catch(() => {});
    }).catch(() => toast.error("Session not found"));
  }, [id]);

  const pdfUrl = `${API}/sessions/${id}/report.pdf?auth=${getToken()}`;

  const downloadPdf = () => {
    window.open(pdfUrl, "_blank");
  };

  const openEmail = () => setEmailOpen(true);

  if (!s) return <Layout><div className="text-ink-500">Loading…</div></Layout>;

  const drop = (s.outcome?.pain_before != null && s.outcome?.pain_after != null)
    ? s.outcome.pain_before - s.outcome.pain_after : null;
  let painChangeLabel = "—";
  if (drop != null) painChangeLabel = drop > 0 ? `↓ ${drop}` : drop;

  return (
    <Layout>
      <Link to={`/clients/${s.client_id}`} className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900 mb-6" data-testid="back-to-client-detail">
        <ArrowLeft className="w-4 h-4" /> Back to client
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Calendar className="w-4 h-4 text-ink-500" />
        <div className="label-overline">Session</div>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-2">
        {new Date(s.created_at).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
      </h1>
      <div className="text-ink-500 mb-6">{s.duration_minutes ? `${s.duration_minutes} minute session` : ""}{client?.name ? ` · ${client.name}` : ""}</div>

      {/* Report actions */}
      <DisclaimerBanner className="mb-4 print:hidden" />
      <div className="flex flex-wrap items-center gap-2 mb-10 print:hidden" data-testid="report-actions">
        <button
          data-testid="download-pdf-btn"
          onClick={downloadPdf}
          className="min-h-[44px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 transition-all shadow-sm hover:shadow-md"
        >
          <FileText className="w-4 h-4" /> Open PDF report
        </button>
        <button
          data-testid="download-pdf-save-btn"
          onClick={() => {
            const a = document.createElement("a");
            a.href = pdfUrl;
            a.download = `posturra-session-${(client?.name || "patient").replace(/\s+/g, "_")}-${(s.created_at || "").slice(0, 10)}.pdf`;
            a.click();
          }}
          className="min-h-[44px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 font-medium inline-flex items-center gap-2 transition-all"
        >
          <Download className="w-4 h-4" /> Download
        </button>
        <button
          data-testid="email-report-btn"
          onClick={openEmail}
          className="min-h-[44px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 font-medium inline-flex items-center gap-2 transition-all"
        >
          <Mail className="w-4 h-4" /> Email
        </button>
        <button
          data-testid="print-btn"
          onClick={() => window.print()}
          className="min-h-[44px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 font-medium inline-flex items-center gap-2 transition-all"
        >
          <Printer className="w-4 h-4" /> Print
        </button>
        {s.client_id && (
          <Link
            to={`/clients/${s.client_id}/compare?to=${s.id}&preset=baseline`}
            data-testid="open-compare-btn"
            className="min-h-[44px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 font-medium inline-flex items-center gap-2 transition-all print:hidden"
          >
            <Calendar className="w-4 h-4" /> Compare with another session
          </Link>
        )}
        {/* Iter AG — completed session with cached recommendations should expose the Send-to-Client flow.
            Gated on generated_at so an accidentally-stored empty {} bundle doesn't render the button. */}
        {s.outcome?.recommendations?.generated_at && (
          <button
            type="button"
            data-testid="send-recommendations-btn"
            onClick={() => setSendRecsOpen(true)}
            className="min-h-[44px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 transition-all print:hidden"
          >
            <Send className="w-4 h-4" /> Send recommendations
          </button>
        )}
      </div>

      {/* Photos */}
      {s.imported_from_history && (
        <div
          className="mb-4 rounded-2xl bg-amber-50/80 border border-amber-200 px-4 py-3 text-xs text-amber-900 print:hidden flex items-start gap-2"
          data-testid="historical-photos-banner"
        >
          <Upload className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold">Imported session.</span> Upload baseline + post-treatment photos and any movement screens to complete the record.
          </div>
        </div>
      )}
      <div className="mb-10 print:hidden">
        <SessionPhotosManager
          session={{ ...s, client_name: client?.name }}
          onSessionUpdate={(updated) => setS(updated)}
        />
      </div>

      {/* AI Analysis */}
      {s.ai_analysis && (
        <div className="mb-10">
          <h2 className="font-heading text-2xl font-medium text-ink-900 mb-4">AI analysis</h2>
          <div className="bg-white border border-paper-200 rounded-3xl p-6 mb-4">
            <div className="label-overline mb-2">Summary</div>
            <p className="text-ink-900">{s.ai_analysis.summary}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-paper-200 rounded-3xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-terracotta-500" />
                <div className="label-overline text-terracotta-600">Problem areas</div>
              </div>
              <div className="space-y-2.5">
                {(s.ai_analysis.problem_areas || []).map((p, i) => (
                  <div key={`pa-${p.area || i}`} className="border-l-2 border-terracotta-500 pl-4">
                    <div className="font-medium text-ink-900">{p.area}</div>
                    <div className="text-sm text-ink-700">{p.description}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-paper-200 rounded-3xl p-6">
              <div className="label-overline mb-3">Recommended techniques</div>
              <div className="space-y-2">
                {(s.ai_analysis.recommended_techniques || []).map((t, i) => (
                  <div key={`rt-${t.technique || i}`} className="bg-paper-50 rounded-xl p-3 border border-paper-200">
                    <div className="font-medium text-sm text-ink-900">{t.technique}</div>
                    <div className="text-xs text-sage-700">{t.target}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Body diagram */}
      <div className="mb-10">
        <h2 className="font-heading text-2xl font-medium text-ink-900 mb-2">Areas worked</h2>
        <AreasWorkedCards workedAreas={s.worked_areas || EMPTY_FOCUS} />
        {s.worked_areas?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {s.worked_areas.map((m) => (
              <span key={m} className="px-3 py-1.5 rounded-full bg-terracotta-500/10 text-terracotta-600 text-sm">
                {MUSCLE_LABELS[m] || m}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* SOAP */}
      <div className="mb-10">
        <h2 className="font-heading text-2xl font-medium text-ink-900 mb-4">SOAP notes</h2>
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 divide-y divide-paper-200">
          {[
            { key: "subjective", label: "S — Subjective" },
            { key: "objective", label: "O — Objective" },
            { key: "assessment", label: "A — Assessment" },
            { key: "plan", label: "P — Plan" },
          ].map((f) => (
            <div key={f.key} className="py-4 first:pt-0 last:pb-0">
              <div className="label-overline mb-1.5">{f.label}</div>
              <div className="text-ink-900 whitespace-pre-wrap" data-testid={`view-soap-${f.key}`}>{s.soap?.[f.key] || <span className="text-ink-500">—</span>}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Wrap-up: home care + return-visit plan */}
      {s.wrap_up && ((s.wrap_up.home_care || []).length > 0 || s.wrap_up.therapist_summary) && (
        <div className="mb-10" data-testid="wrapup-view">
          <h2 className="font-heading text-2xl font-medium text-ink-900 mb-4">Wrap-up</h2>
          {s.wrap_up.therapist_summary && (
            <div className="bg-white border border-paper-200 rounded-3xl p-6 mb-4">
              <div className="label-overline mb-2">Therapist debrief</div>
              <p className="text-ink-700 leading-relaxed">{s.wrap_up.therapist_summary}</p>
            </div>
          )}
          {(s.wrap_up.home_care || []).length > 0 && (
            <div className="bg-white border border-paper-200 rounded-3xl p-6 mb-4">
              <div className="label-overline mb-3">Home care for the client</div>
              <ul className="space-y-2">
                {s.wrap_up.home_care.map((item, i) => (
                  <li key={`hc-${i}-${item.slice(0, 20)}`} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-sage-500 text-white text-xs flex items-center justify-center shrink-0 mt-0.5 font-medium">{i + 1}</span>
                    <span className="text-ink-900 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {s.wrap_up.best_return?.when && (
              <div className="rounded-2xl border border-sage-500/40 bg-sage-500/5 p-5">
                <div className="text-[10px] uppercase tracking-[0.14em] font-medium px-2.5 py-1 rounded-full bg-sage-500 text-white inline-block mb-3">Best — recommended</div>
                <div className="font-heading text-2xl font-light text-ink-900 mb-2">{s.wrap_up.best_return.when}</div>
                <p className="text-sm text-ink-700">{s.wrap_up.best_return.why}</p>
              </div>
            )}
            {s.wrap_up.good_return?.when && (
              <div className="rounded-2xl border border-terracotta-500/30 bg-terracotta-500/5 p-5">
                <div className="text-[10px] uppercase tracking-[0.14em] font-medium px-2.5 py-1 rounded-full bg-terracotta-500 text-white inline-block mb-3">Good alternative</div>
                <div className="font-heading text-2xl font-light text-ink-900 mb-2">{s.wrap_up.good_return.when}</div>
                <p className="text-sm text-ink-700">{s.wrap_up.good_return.why}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Outcome */}
      {s.outcome && (
        <div className="mb-10">
          <h2 className="font-heading text-2xl font-medium text-ink-900 mb-4">Outcome</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Stat label="Pain before" value={s.outcome.pain_before ?? "—"} />
            <Stat label="Pain after" value={s.outcome.pain_after ?? "—"} />
            <Stat label="Pain change" value={painChangeLabel} highlight />
            <Stat label="Effectiveness" value={s.outcome.effectiveness ? `${s.outcome.effectiveness}/5` : "—"} />
          </div>
          {s.outcome.techniques_used?.length > 0 && (
            <div className="bg-white border border-paper-200 rounded-3xl p-6 mb-4">
              <div className="label-overline mb-3">Techniques used</div>
              <div className="flex flex-wrap gap-2">
                {s.outcome.techniques_used.map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-full bg-sage-100 text-sage-700 text-sm">{t}</span>
                ))}
              </div>
            </div>
          )}
          {s.outcome.client_feedback && (
            <div className="bg-white border border-paper-200 rounded-3xl p-6">
              <div className="label-overline mb-2">Client feedback</div>
              <p className="text-ink-900 italic">&ldquo;{s.outcome.client_feedback}&rdquo;</p>
            </div>
          )}
        </div>
      )}
      {emailOpen && client && (
        <EmailReportModal
          sessionId={id}
          client={client}
          createdAt={s.created_at}
          pdfUrl={pdfUrl}
          onClose={() => setEmailOpen(false)}
        />
      )}
      <SendToClientModal
        open={sendRecsOpen}
        onClose={() => setSendRecsOpen(false)}
        sessionId={id}
        bundle={s.outcome?.recommendations || null}
        client={client}
      />
    </Layout>
  );
}

function EmailReportModal({ sessionId, client, createdAt, pdfUrl, onClose }) {
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const dateStr = new Date(createdAt).toLocaleDateString();

  const send = async (e) => {
    e.preventDefault();
    if (!recipient) {
      toast.error("Recipient email is required.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/sessions/${sessionId}/email-report`, { recipient_email: recipient, message });
      toast.success(`Sent to ${recipient}.`);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "email_not_configured") {
        // Fall back to mailto.
        toast.message("Email service not yet enabled — opening your mail app instead.");
        const subject = encodeURIComponent(`Clinical Session Report — ${client.name} — ${dateStr}`);
        const body = encodeURIComponent(
          `Hello,\n\n${message || ""}\n\nDownload link (requires my session): ${pdfUrl}\n\nThank you,`
        );
        window.location.href = `mailto:${encodeURIComponent(recipient)}?subject=${subject}&body=${body}`;
        onClose();
      } else {
        toast.error(detail || "Could not send.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="email-modal" className="fixed inset-0 z-50 bg-ink-900/40 flex items-end md:items-center justify-center p-0 md:p-4">
      <form onSubmit={send} className="bg-white w-full md:max-w-md md:rounded-3xl rounded-t-3xl p-6 space-y-3 max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl text-ink-900">Email session report</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-paper-100" data-testid="email-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs text-ink-500">Sending <strong>{client.name}</strong> · {dateStr}</div>
        <label className="block text-sm">
          <div className="label-overline mb-1.5">Recipient email</div>
          <input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            data-testid="email-recipient"
            placeholder="client@example.com"
            className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200"
            autoFocus
          />
        </label>
        <label className="block text-sm">
          <div className="label-overline mb-1.5">Custom message (optional)</div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="email-message"
            rows={4}
            placeholder="Hi — attaching your session notes. Let me know if you have questions."
            className="w-full px-3 py-2 rounded-2xl border border-paper-200"
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          data-testid="email-send-btn"
          className="w-full min-h-[44px] rounded-full bg-ink-900 text-white font-medium hover:bg-ink-800 inline-flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send via Posturra"}
        </button>
        <p className="text-[11px] text-ink-500 text-center">
          If our email service isn&apos;t configured we&apos;ll open your mail app instead.
        </p>
      </form>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 border ${highlight ? "bg-sage-500 border-sage-500 text-white" : "bg-white border-paper-200"}`}>
      <div className={`text-xs uppercase tracking-[0.14em] ${highlight ? "opacity-80" : "text-ink-500"}`}>{label}</div>
      <div className={`font-heading text-2xl font-medium mt-1 ${highlight ? "" : "text-ink-900"}`}>{value}</div>
    </div>
  );
}
